/**
 * useStatementActions.js
 * Custom hook for managing statement actions and dialogs
 */

import { useState } from 'react';

export const useStatementActions = (onStatementSelect, onStatementDelete, onBulkUnreconcile) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStatementForAction, setSelectedStatementForAction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [bulkUnreconcileDialogOpen, setBulkUnreconcileDialogOpen] = useState(false);

  const handleMenuOpen = (event, statement) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedStatementForAction(statement);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStartReconciliation = (statement) => {
    onStatementSelect(statement);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDetailsClick = () => {
    setDetailsDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (selectedStatementForAction) {
      try {
        await onStatementDelete(selectedStatementForAction.id);
        setDeleteDialogOpen(false);
        setSelectedStatementForAction(null);
      } catch (error) {
        console.error('Error deleting statement:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedStatementForAction(null);
  };

  const handleDetailsClose = () => {
    setDetailsDialogOpen(false);
    setSelectedStatementForAction(null);
  };

  const handleStartFromDetails = () => {
    if (selectedStatementForAction) {
      handleStartReconciliation(selectedStatementForAction);
      handleDetailsClose();
    }
  };

  const handleBulkUnreconcileClick = () => {
    setBulkUnreconcileDialogOpen(true);
    handleMenuClose();
  };

  const handleBulkUnreconcileConfirm = async () => {
    if (!selectedStatementForAction || !onBulkUnreconcile) return;
    try {
      await onBulkUnreconcile(selectedStatementForAction.id);
      setBulkUnreconcileDialogOpen(false);
      setSelectedStatementForAction(null);
    } catch (error) {
      // Error toast is already shown by the hook; keep dialog open
      console.error('Error bulk unreconciling statement:', error);
    }
  };

  const handleBulkUnreconcileClose = () => {
    setBulkUnreconcileDialogOpen(false);
    setSelectedStatementForAction(null);
  };

  return {
    // Menu state
    anchorEl,
    selectedStatementForAction,

    // Dialog states
    deleteDialogOpen,
    detailsDialogOpen,
    bulkUnreconcileDialogOpen,

    // Handlers
    handleMenuOpen,
    handleMenuClose,
    handleStartReconciliation,
    handleDeleteClick,
    handleDetailsClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleDetailsClose,
    handleStartFromDetails,
    handleBulkUnreconcileClick,
    handleBulkUnreconcileConfirm,
    handleBulkUnreconcileClose,
  };
};
